import { Formik, FormikProps } from 'formik';
import { ScrollToFieldError } from '@synergy/components';
import { Stack } from '@mui/material';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { forwardRef, useEffect, useRef } from 'react';

import * as CustomerConditionActions from './store/customer-conditions-actions';
import { CustomerConditionsFormValues } from './models/customer-conditions-form-values';
import { CustomerDetailConditionsBonus } from './components/customer-detail-conditions-bonus';
import { CustomerDetailConditionsDlh } from './components/customer-detail-conditions-dlh';
import { CustomerDetailConditionsMemberSince } from './components/customer-detail-conditions-member-since';
import { CustomerDetailConditionsPayment } from './components/customer-detail-conditions-payment';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import {
    customerConditionSelector,
    customerDetailConditionsBaselineSelector,
    customerDetailConditionsFormDataSelector,
} from './store/customer-conditions-selectors';
import { customerDetailConditionsValidationSchema } from './customer-detail-conditions-validation-schema';
import { customerDetailOverviewSelector } from '../overview/store/customer-detail-overview-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const CustomerDetailConditions = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const { conditions } = useAppSelector(customerConditionSelector);
    const { customer } = useAppSelector(customerDetailOverviewSelector);
    const dispatch = useThunkDispatch();
    const formikRef = useRef<FormikProps<CustomerConditionsFormValues>>(null);
    const { formValues, errors, touched } = useAppSelector(customerDetailConditionsFormDataSelector);
    // The conditions as the server has them — see PaloDirtyComparison.
    const baseline = useAppSelector(customerDetailConditionsBaselineSelector);

    useEffect(() => {
        if (!conditions) {
            dispatch(CustomerConditionActions.fetchCustomerConditions());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(CustomerConditionActions.updateCustomerConditions(values, isDirty, isValid, errs));
    });

    if (!conditions) {
        return null;
    }

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            initialValues={formValues}
            validationSchema={customerDetailConditionsValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formikRef}
        >
            <Stack gap={6}>
                <PaloDirtyComparison baseline={baseline} />
                <ScrollToFieldError />
                <CustomerDetailConditionsMemberSince />
                <CustomerDetailConditionsDlh />
                <CustomerDetailConditionsPayment />
                <CustomerDetailConditionsBonus />
            </Stack>
        </Formik>
    );
});
